import { GridColDef, GridRowsProp } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';

import {
  AbsenceEntry,
  AbsenceHistory,
  AbsenceSupersededReason,
  AbsenceType,
} from '~/data/absences/types';
import {
  AbsenceHistoryTableRow,
  getAbsenceDataRowCommonFromAbsenceEntry,
} from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/data';
import { useCommonAbsenceTableColumnConfigs } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/AbsencesTable/data/useCommonAbsenceTableColumnConfigs';

type PropsType = {
  absenceType: AbsenceType;
  absencesHistory: AbsenceHistory<AbsenceEntry>[];
};

type UseAbsenceHistoryTableOutput = {
  rows: GridRowsProp<AbsenceHistoryTableRow>;
  columnConfigs: GridColDef<AbsenceHistoryTableRow>[];
};

export const useAbsenceHistoryTable = (
  props: PropsType,
): UseAbsenceHistoryTableOutput => {
  const { absencesHistory } = props;

  const rows: GridRowsProp<AbsenceHistoryTableRow> = useMemo(() => {
    const getRowData = (
      entry: AbsenceEntry,
      index?: number,
    ): AbsenceHistoryTableRow => {
      const { supersededReason, supersededDate, enteredTime } = entry;

      const entryRow = getAbsenceDataRowCommonFromAbsenceEntry(entry);

      const absenceId = entry.uid ?? '';

      const entryOverride =
        supersededReason === AbsenceSupersededReason.DELETE
          ? {
              status: supersededReason,
              created: supersededDate,
            }
          : {
              created: enteredTime,
            };

      return {
        hierarchy:
          typeof index !== 'undefined' ? [absenceId, `${index}`] : [absenceId],
        ...entryRow,
        id: `${absenceId}-${index}`,
        ...entryOverride,
      };
    };

    return absencesHistory.reduce((acc, entry) => {
      const { history, ...mainEntry } = entry;

      return [
        ...acc,
        getRowData(mainEntry),
        ...history.map((historyEntry, index) =>
          getRowData(historyEntry, index),
        ),
      ];
    }, [] as AbsenceHistoryTableRow[]);
  }, [absencesHistory]);

  const { columnConfigs } =
    useCommonAbsenceTableColumnConfigs<AbsenceHistoryTableRow>({
      isHistory: true,
    });

  return {
    rows,
    columnConfigs,
  };
};
